import { useAction } from "convex/react";
import { useCallback, useEffect, useState } from "react";

import { api } from "@convex/_generated/api";
import type { StudioApp } from "@domain/studio-app";
import {
  studioAppDeleteFailed,
  studioAppNotFound,
  studioAppReadFailed,
  studioAppsLoadFailed,
  studioAppTooLarge,
} from "@domain/user-errors";
import { errorCodeText } from "@/lib/convexErrors";
import { logMutationError } from "@/lib/logger";

/**
 * Studio owner hooks over the `studioActions` surface (studio spec §4.4):
 * `useStudioApps()` (library list + delete) and `useStudioApp(appDir)`
 * (detail read). Action-backed — the GCS `studio/` prefix is the sole source
 * of truth (path-as-identity, spec R3), so there is no reactive query here:
 * fetch-on-mount + manual `refresh`, with the visible `lastRefreshedAt`
 * affordance owning the staleness contract (same carve-out as `useNotebook`).
 * NO timers, NO auto-retry, NO polling (spec §1.2) — refetch is user- or
 * event-driven (the turn-end signal wires into `refresh` at the route layer).
 */

/** Share state joined onto a list row (`listApps` server-side join). */
export interface StudioShareInfo {
  shareName: string;
  active: boolean;
  url: string;
}

/**
 * One app's list-view row (spec §4.2 row 5 `listApps`). Only the entry HTML
 * is carried (thumbnail + title), and only when under the server's inline
 * cap — full file bodies come lazily via `useStudioApp`.
 */
export interface StudioAppListItem {
  appDir: string;
  name: string;
  /** Max object mtime across the app's files (ms epoch); null when unknown. */
  updatedAt: number | null;
  sizeBytes: number;
  fileCount: number;
  /** Entry file body when under the inline cap; null otherwise (honest degrade). */
  entryHtml: string | null;
  share: StudioShareInfo | null;
}

/**
 * A fully-read app (`readApp`): every file body keyed by app-relative path.
 * The action result IS the finalized domain contract — re-exported under the
 * hook-local name its consumers already use.
 */
export type StudioAppDetail = StudioApp;

// ─── Coded-error → copy mapping ──────────────────────────────────────
// Actions throw `ConvexError(CODE)` — the ONLY throw shape whose payload
// survives to the client on a production deployment (a plain `Error` message
// is redacted to "Server Error", which used to collapse every code here to
// UNKNOWN in prod). `errorCodeText` reads `.data`, falling back to the message
// so dev throw sites still map. A transport (502-class) throw carries neither
// and falls through to the operation's generic copy.

function messageOf(error: unknown): string {
  return errorCodeText(error);
}

/** Machine-readable failure class — the detail route's tri-state branches on
 *  `NOT_FOUND` without string-matching display copy. */
export type StudioAppErrorCode =
  | "NOT_FOUND"
  | "APP_FILE_TOO_LARGE"
  | "APP_TOO_LARGE"
  | "INVALID_APP_PATH"
  | "UNKNOWN";

function codeOf(error: unknown): StudioAppErrorCode {
  const msg = messageOf(error);
  if (msg.includes("APP_FILE_TOO_LARGE")) return "APP_FILE_TOO_LARGE";
  if (msg.includes("APP_TOO_LARGE")) return "APP_TOO_LARGE";
  if (msg.includes("INVALID_APP_PATH")) return "INVALID_APP_PATH";
  if (msg.includes("NOT_FOUND")) return "NOT_FOUND";
  return "UNKNOWN";
}

function readErrorCopy(code: StudioAppErrorCode): string {
  switch (code) {
    // An invalid appDir (route-param junk) can never name a real app — the
    // serve path 404s it opaquely (§5); the owner UI says "not found" too.
    case "NOT_FOUND":
    case "INVALID_APP_PATH":
      return studioAppNotFound();
    case "APP_FILE_TOO_LARGE":
    case "APP_TOO_LARGE":
      return studioAppTooLarge();
    default:
      return studioAppReadFailed();
  }
}

function deleteErrorCopy(code: StudioAppErrorCode): string {
  return code === "INVALID_APP_PATH" ? studioAppNotFound() : studioAppDeleteFailed();
}

/** Thrown by `deleteApp` so callers can surface copy inline without
 *  re-deriving it. `.userMessage` is the display string. */
export class StudioAppError extends Error {
  readonly userMessage: string;
  readonly code: StudioAppErrorCode;
  readonly reason: unknown;
  constructor(userMessage: string, code: StudioAppErrorCode, reason: unknown) {
    super(userMessage);
    this.name = "StudioAppError";
    this.userMessage = userMessage;
    this.code = code;
    this.reason = reason;
  }
}

// ─── Hooks ───────────────────────────────────────────────────────────

export interface UseStudioApps {
  apps: StudioAppListItem[];
  isLoading: boolean;
  /** User-facing copy for the most recent list-load failure, or null. */
  error: string | null;
  /** Timestamp (ms) of the last successful list load, or null before the first. */
  lastRefreshedAt: number | null;
  refresh: () => Promise<void>;
  deleteApp: (args: { appDir: string }) => Promise<void>;
}

/**
 * Library list surface. Fetch-on-mount + manual `refresh`; a load failure
 * surfaces copy in `error` (never throws). `deleteApp` is imperative and
 * throws `StudioAppError` — callers refresh the list after a green delete.
 */
export function useStudioApps(): UseStudioApps {
  const listAppsAction = useAction(api.studioActions.listApps);
  const deleteAppAction = useAction(api.studioActions.deleteApp);

  const [apps, setApps] = useState<StudioAppListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listAppsAction({});
      setApps(result?.apps ?? []);
      setLastRefreshedAt(Date.now());
    } catch (err) {
      logMutationError({ mutation: "studioActions.listApps", error: err });
      setError(studioAppsLoadFailed());
    } finally {
      setIsLoading(false);
    }
  }, [listAppsAction]);

  // Fetch-on-mount. No interval, no retry — refetch is user- or event-driven.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const deleteApp = useCallback(
    async ({ appDir }: { appDir: string }): Promise<void> => {
      try {
        await deleteAppAction({ appDir });
      } catch (err) {
        logMutationError({
          mutation: "studioActions.deleteApp",
          args: { appDir },
          error: err,
        });
        const code = codeOf(err);
        throw new StudioAppError(deleteErrorCopy(code), code, err);
      }
    },
    [deleteAppAction],
  );

  return { apps, isLoading, error, lastRefreshedAt, refresh, deleteApp };
}

export interface UseStudioApp {
  /** The last successfully read app; null before the first load and after a
   *  NOT_FOUND (a transport blip keeps the last good preview visible). */
  app: StudioAppDetail | null;
  isLoading: boolean;
  /** User-facing copy for the most recent read failure, or null. */
  error: string | null;
  /** Branch key for the detail route's tri-state (NOT_FOUND → not-found pane). */
  errorCode: StudioAppErrorCode | null;
  /** Timestamp (ms) of the last successful read, or null before the first. */
  lastRefreshedAt: number | null;
  refresh: () => Promise<void>;
}

/**
 * Detail read surface for one app. Fetch-on-mount + manual `refresh` (the
 * turn-end signal and the preview's refresh affordance both call it). On a
 * refresh failure the last good `app` stays rendered UNLESS the app is gone
 * (`NOT_FOUND`/`INVALID_APP_PATH`) — honest tri-state without a flickering
 * preview.
 */
export function useStudioApp(appDir: string): UseStudioApp {
  const readAppAction = useAction(api.studioActions.readApp);

  const [app, setApp] = useState<StudioAppDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<StudioAppErrorCode | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      const result = await readAppAction({ appDir });
      setApp({
        appDir: result.appDir,
        name: result.name,
        entry: result.entry,
        files: result.files,
        updatedAt: result.updatedAt,
      });
      setLastRefreshedAt(Date.now());
    } catch (err) {
      logMutationError({
        mutation: "studioActions.readApp",
        args: { appDir },
        error: err,
      });
      const code = codeOf(err);
      setError(readErrorCopy(code));
      setErrorCode(code);
      if (code === "NOT_FOUND" || code === "INVALID_APP_PATH") setApp(null);
    } finally {
      setIsLoading(false);
    }
  }, [readAppAction, appDir]);

  // Fetch-on-mount (and on appDir change — `refresh` is keyed on it).
  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { app, isLoading, error, errorCode, lastRefreshedAt, refresh };
}
